import { Star, ExternalLink } from "lucide-react";
import { SITE } from "../lib/site";

/**
 * Google Reviews Badge — two variants.
 * variant="inline"   → compact pill (for hero, header)
 * variant="full"     → full card (for testimonials section)
 */
export default function GoogleReviewsBadge({ variant = "inline", invert = false }) {
    const { googleReviewsUrl, googleRating, googleReviewCount } = SITE;
    if (!googleReviewsUrl) return null;

    const stars = Array.from({ length: 5 });

    if (variant === "inline") {
        return (
            <a
                href={googleReviewsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="google-reviews-inline"
                className={`inline-flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 ${
                    invert
                        ? "bg-white/10 backdrop-blur-md border border-white/25 text-white hover:bg-white/20"
                        : "bg-white border border-border text-foreground hover:border-primary shadow-sm"
                }`}
            >
                <GoogleG size={18} />
                <span className="flex items-center gap-1">
                    <strong className="font-heading font-bold">{googleRating.toFixed(1)}</strong>
                    <span className="flex items-center gap-0.5">
                        {stars.map((_, i) => (
                            <Star
                                key={i}
                                size={13}
                                className={`${invert ? "text-secondary" : "text-amber-500"} fill-current`}
                            />
                        ))}
                    </span>
                </span>
                <span className={`${invert ? "text-white/80" : "text-muted-foreground"} hidden sm:inline`}>
                    {googleReviewCount} reviews on Google
                </span>
            </a>
        );
    }

    // Full card
    return (
        <a
            href={googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-full"
            className="group block bg-card border border-border rounded-lg p-8 hover:shadow-md transition-all hover:-translate-y-0.5"
        >
            <div className="flex items-start gap-5">
                <div className="h-14 w-14 rounded-full bg-muted grid place-items-center shrink-0 shadow-sm">
                    <GoogleG size={28} />
                </div>
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                        Rated on Google
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                        <div className="text-4xl font-heading font-extrabold tracking-tight text-foreground">
                            {googleRating.toFixed(1)}
                        </div>
                        <div className="flex items-center gap-0.5">
                            {stars.map((_, i) => (
                                <Star key={i} size={18} className="text-amber-500 fill-amber-500" />
                            ))}
                        </div>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                        Based on {googleReviewCount} verified reviews
                    </div>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all">
                        Read & write reviews <ExternalLink size={14} />
                    </div>
                </div>
            </div>
        </a>
    );
}

/** Google "G" brand mark — simple SVG, no external deps */
function GoogleG({ size = 18 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 48 48"
            aria-hidden="true"
        >
            <path
                fill="#FFC107"
                d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
                fill="#FF3D00"
                d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
                fill="#4CAF50"
                d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
                fill="#1976D2"
                d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
        </svg>
    );
}
